import { useEditProtection } from '../../shared/components/EditProtection';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import './CashWithdrawal.css';

interface User {
  id: number;
  name: string;
  total_cash: number;
}

const CashWithdrawal: React.FC = () => {
  const navigate = useNavigate();
  const { user, sessionToken } = useAuth();
  const { showToast, fetchNotifications } = useNotification();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const isAdminOrManager = user && ['Owner', 'Admin', 'Manager'].includes(user.role);

  const fetchUsers = useCallback(async () => {
    // Only Admin/Manager can see all users
    if (!isAdminOrManager) {
      // For Employees, auto-select themselves
      if (user) {
        setUsers([{ id: user.id, name: user.name, total_cash: 0 }]);
        setSelectedUserId(user.id);
      }
      return;
    }

    try {
      const response = await fetch('/api/users', {
        headers: {
          'X-Session-Token': sessionToken || ''
        },
        cache: 'no-store'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [sessionToken, isAdminOrManager, user]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const [saved, setSaved] = useState(false);
  useEditProtection(!saved && (!!amount || !!reason));
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!selectedUserId) {
      newErrors.selectedUserId = 'Please select who is taking the cash';
    }

    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (!reason.trim()) {
      newErrors.reason = 'Reason for withdrawal is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!window.confirm(`Confirm $${Number(amount).toFixed(2)} for ${users.find(u => u.id === selectedUserId)?.name || user?.name}?`)) return;
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/cash-withdrawal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken || ''
        },
        body: JSON.stringify({
          recipientUserId: selectedUserId,
          recipientName: selectedUser?.name || '',
          amount: parseFloat(amount),
          reason: reason.trim()
        }),
        cache: 'no-store'  // ✅ Prevent caching of POST requests
      });

      if (!response.ok) {
        throw new Error('Failed to record cash withdrawal');
      }

      const data = await response.json();
      showToast({
        type: 'success',
        title: 'Cash Withdrawal',
        message: `${user?.name} withdrew $${parseFloat(amount).toFixed(2)} to ${selectedUser?.name}`
      });
      fetchNotifications();
      setSaved(true);
      setTimeout(() => navigate('/cash', {
        state: {
          toast: {
            type: 'success',
            title: 'Cash Withdrawal Recorded',
            message: data.message,
          },
        },
      }), 0);
    } catch (error) {
      console.error('Error recording cash withdrawal:', error);
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to record cash withdrawal. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  return (
    <div className="cash-withdrawal">
      <div className="header">
        <button onClick={() => navigate('/cash/new')} className="back-btn">← Back</button>
        <h2>Cash Withdrawal</h2>
      </div>

      <div className="withdrawal-info">
        <p>Record cash being taken from the till. This will track who has the cash and reduce the till balance.</p>
      </div>

      <form onSubmit={handleSubmit} className="withdrawal-form">
        <div className="form-group">
          <label htmlFor="selectedUserId">Who is taking the cash? *</label>
          {isAdminOrManager ? (
            <>
              <select
                id="selectedUserId"
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
                className={errors.selectedUserId ? 'error' : ''}
              >
                <option value="">Select a user</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} (Current Balance: ${(Number(u.total_cash) || 0).toFixed(2)})
                  </option>
                ))}
              </select>
              {errors.selectedUserId && <span className="error-text">{errors.selectedUserId}</span>}
              {users.length === 0 && (
                <small className="info-text">Loading users...</small>
              )}
            </>
          ) : (
            <div className="readonly-user">
              <span className="user-name">{user?.name}</span>
              <small className="info-text">Recording withdrawal for yourself</small>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="amount">Amount (CAD) *</label>
          <div className="amount-input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="text" inputMode="decimal"
              id="amount"
              value={amount}
              onChange={handleAmountChange}
              className={errors.amount ? 'error amount-input' : 'amount-input'}
              placeholder="0.00"
              maxLength={10}
            />
          </div>
          {errors.amount && <span className="error-text">{errors.amount}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="reason">Reason for withdrawal *</label>
          <textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={errors.reason ? 'error' : ''}
            placeholder="e.g., Bank deposit, petty cash, supplier payment..."
            rows={3}
            maxLength={500}
          />
          {errors.reason && <span className="error-text">{errors.reason}</span>}
          <small className="char-count">{reason.length}/500 characters</small>
        </div>

        <div className="withdrawal-summary">
          <h3>Summary</h3>
          <div className="summary-row">
            <span>Recipient:</span>
            <span>{selectedUser?.name || 'Not selected'}</span>
          </div>
          <div className="summary-row">
            <span>Amount:</span>
            <span className="amount-display">${amount || '0.00'}</span>
          </div>
          <div className="summary-row">
            <span>Reason:</span>
            <span>{reason || 'Not specified'}</span>
          </div>
          {selectedUser && (
            <div className="summary-row">
              <span>New Balance:</span>
              <span className="balance-display">
                ${((Number(selectedUser.total_cash) || 0) + parseFloat(amount || '0')).toFixed(2)}
              </span>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={isSubmitting}
            className="submit-btn"
          >
            {isSubmitting ? 'Recording...' : 'Record Withdrawal'}
          </button>
        </div>
      </form>

      <div className="important-note">
        <h4>📋 Important</h4>
        <p>After recording this withdrawal, continue to use the app to track any transactions made with this cash. This helps maintain accurate cash balances for each person.</p>
      </div>
    </div>
  );
};

export default CashWithdrawal;